import { ZUid, zUid } from "src/types/zUid";
import { z } from "zod";

export const zSchedSettingsTask = z.object({
  templateObjectId: zUid.nullable(),
  nameAttributeId: zUid.nullable(),
  durationAttributeId: zUid.nullable(),
  groupAttributeId: zUid.nullable(),
  priorityAttributeId: zUid.nullable(),
  // Ссылки на другие объекты, участвующие в формировании таблицы
  modelLinkAttributeId: zUid.nullable(),
  relationLinkAttributeId: zUid.nullable(),
  performerLinkAttributeId: zUid.nullable(),
  divisionLinkAttributeId: zUid.nullable(),
  planLinkAttributeId: zUid.nullable(),
  // Привязки
  relationObjectId: zUid.nullable(),
  previousAttributeId: zUid.nullable(),
  currentAttributeId: zUid.nullable(),
  relationTypeAttributeId: zUid.nullable(),
  deltaDaysAttributeId: zUid.nullable(),
  // Сроки
  plannedStartDateAttributeId: zUid.nullable(),
  plannedEndDateAttributeId: zUid.nullable(),
  settlementStartDateAttributeId: zUid.nullable(),
  settlementEndDateAttributeId: zUid.nullable(),
  actualStartDateAttributeId: zUid.nullable(),
  actualEndDateAttributeId: zUid.nullable(),
  // Настройки поставщика
  supplierObjectId: zUid.nullish(),
  supplierRelationAttributeId: zUid.nullish(),
  supplierNameAttributeId: zUid.nullish(),
  // Настройки исполнителя
  performerObjectId: zUid.nullable(),
  performerLoadAttributeId: zUid.nullable(),
});
export type ZSchedSettingsTask = z.infer<typeof zSchedSettingsTask>;

export const zSchedSettingsTaskTemplate = zSchedSettingsTask
  .pick({
    templateObjectId: true,
    nameAttributeId: true,
    durationAttributeId: true,
    groupAttributeId: true,
    relationObjectId: true,
    previousAttributeId: true,
    currentAttributeId: true,
    relationTypeAttributeId: true,
    deltaDaysAttributeId: true,
    performerLinkAttributeId: true,
    divisionLinkAttributeId: true,
  })
  .extend({
    showByCondition: z.boolean().nullable().optional(),
  });
export type ZSchedSettingsTaskTemplate = z.infer<
  typeof zSchedSettingsTaskTemplate
>;

export const zSchedSettingsGroup = zSchedSettingsTask
  .pick({
    templateObjectId: true,
    nameAttributeId: true,
  })
  .extend({
    relationPlanTemplateAttributeId: zUid.nullable(),
    taskListAttributeId: zUid.nullable(),
  });
export type ZSchedSettingsGroup = z.infer<typeof zSchedSettingsGroup>;

export const zSchedSettingsPlanTemplate = zSchedSettingsTask
  .pick({
    templateObjectId: true,
    nameAttributeId: true,
  })
  .extend({
    groupTaskListAttributeId: zUid.nullable(),
  });
export type ZSchedSettingsPlanTemplate = z.infer<
  typeof zSchedSettingsPlanTemplate
>;

export const zSchedSettingsPlan = zSchedSettingsPlanTemplate.extend({
  startDateAttributeId: zUid.nullable(),
  modelLinkAttributeId: zUid.nullable(),
});
export type ZSchedSettingsPlan = z.infer<typeof zSchedSettingsPlan>;

export const zSchedulingSettings = z.object({
  taskSettings: zSchedSettingsTask.nullable(),
  taskTemplateSettings: zSchedSettingsTaskTemplate.nullable(),
  groupSettings: zSchedSettingsGroup.nullable(),
  groupTemplateSettings: zSchedSettingsGroup.nullable(),
  planSettings: zSchedSettingsPlan.nullable(),
  planTemplateSettings: zSchedSettingsPlanTemplate.nullable(),
});
export type ZSchedulingSettings = z.infer<typeof zSchedulingSettings>;

export enum SchedRowType {
  plan = "PLAN",
  group = "GROUP",
  task = "TASK",
}

export enum SchedCodeType {
  changed = "CHANGED",
  brokenLink = "BROKEN_LINK",
}
const zSchedCode = z.nativeEnum(SchedCodeType);

export enum SchedStateType {
  created = "CREATED",
  progress = "IN_PROGRESS",
  completed = "COMPLETED",
}

const zSchedState = z.object({
  name: z.nativeEnum(SchedStateType).optional(),
  label: z.string().optional(),
  progress: z.number().optional(),
  code: zSchedCode.nullish(),
});
export type ZSchedState = z.infer<typeof zSchedState>;

export enum SchedPriorityType {
  low = "LOW",
  medium = "MEDIUM",
  high = "HIGH",
}

const zSchedPriority = z.object({
  name: z.nativeEnum(SchedPriorityType),
  label: z.string(),
});
export type ZSchedPriority = z.infer<typeof zSchedPriority>;

const zSchedCodeNumber = zSchedState
  .pick({ code: true })
  .extend({ value: z.number() });
export type ZSchedCodeNumber = z.infer<typeof zSchedCodeNumber>;

const zSchedCodeString = zSchedState
  .pick({ code: true })
  .extend({ value: z.string() });
export type ZSchedCodeString = z.infer<typeof zSchedCodeString>;

export interface ZSchedulingRow {
  id: ZUid;
  type: SchedRowType;
  name: ZSchedCodeString | null;
  state: ZSchedState | null;
  plannedDuration: ZSchedCodeNumber | null;
  actualDuration: number | null;
  plannedStartDate: ZSchedCodeString | null;
  plannedEndDate: ZSchedCodeString | null;
  settlementStartDate: string | null;
  settlementEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  supplier: string | null;
  performer: string | null;
  division: string | null;
  priority: ZSchedPriority | null;
  performerLoad: number | null;
  deltaDays: number | null;
  children?: ZSchedulingRow[] | null;
}

export const zSchedulingRow: z.ZodType<ZSchedulingRow> = z.object({
  id: zUid,
  type: z.nativeEnum(SchedRowType),
  name: zSchedCodeString.nullable(),
  state: zSchedState.nullable(),
  plannedDuration: zSchedCodeNumber.nullable(),
  actualDuration: z.number().nullable(),
  plannedStartDate: zSchedCodeString.nullable(),
  plannedEndDate: zSchedCodeString.nullable(),
  settlementStartDate: z.string().nullable(),
  settlementEndDate: z.string().nullable(),
  actualStartDate: z.string().nullable(),
  actualEndDate: z.string().nullable(),
  supplier: z.string().nullable(),
  performer: z.string().nullable(),
  division: z.string().nullable(),
  priority: zSchedPriority.nullable(),
  performerLoad: z.number().nullable(),
  deltaDays: z.number().nullable(),
  children: z.lazy(() => zSchedulingRow.array().optional().nullable()),
});

export enum SchedFunctionType {
  allowTaskDeletion = "allowTaskDeletion",
}

export const zSchedulingPlan = z.object({
  plan: zSchedulingRow,
  functions: z.nativeEnum(SchedFunctionType).array(),
  containStartedTasks: z.boolean().optional(),
});
export type ZSchedulingPlan = z.infer<typeof zSchedulingPlan>;

export const zColumnSettings = z.object({
  hiddenColumns: zUid.array().optional(),
  columnWidths: z.record(zUid, z.number().or(z.string())).optional(),
});
export type ZColumnSettings = z.infer<typeof zColumnSettings>;

// Проверка удаления задач

export enum TaskDeletionError {
  notFound = "TASK_NOT_FOUND",
  inProgress = "TASK_IN_PROGRESS",
  finished = "TASK_FINISHED",
}

export const zRelatedTask = z.object({
  name: z.string().nullable(),
  tasks: z.string().array(),
});
export type ZRelatedTask = z.infer<typeof zRelatedTask>;

export const zTaskDeletion = z.object({
  canDelete: z.boolean(),
  error: z.nativeEnum(TaskDeletionError).nullable(),
  id: zUid,
  name: z.string().nullable(),
  groupName: z.string().nullable(),
  relatedTasks: zRelatedTask.array(),
});
export type ZTaskDeletion = z.infer<typeof zTaskDeletion>;

export const zTasksDeletionCheckInfo = z.object({
  canDelete: z.boolean(),
  tasks: zTaskDeletion.array(),
  recalculatedTasks: zRelatedTask.array(),
});
export type ZTasksDeletionCheckInfo = z.infer<typeof zTasksDeletionCheckInfo>;

export const zTasksDeletionCheck = z.object({
  plan: zSchedulingRow,
  checkDelete: zTasksDeletionCheckInfo,
});
export type ZTasksDeletionCheck = z.infer<typeof zTasksDeletionCheck>;
